import { Flex } from '@chakra-ui/react';
import { Drawer, Text } from '@kula-ai/components';
import { DocusignWordmarkAsset } from '@kula-ai/assets';
// The file, not the folder barrel: ApplicationActions reaches this drawer
// through the `components/ats` barrel, and going back out through the
// esignature barrel closes a cycle that `pnpm circular:check` rejects.
import { EsignActivity } from '@/components/shared/esignature/activity/EsignActivity';

interface EsignAdhocDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  applicationData: Record<string, any>;
  refetchApplication: () => void;
}

/**
 * "Request e-sign via Docusign" — a one-off send from the candidate's Actions
 * menu (Figma 2:14325, drawer at 9:4002 / 37:12252).
 *
 * The same two-step wizard the stage row opens, at the same 1067px, but with no
 * activity behind it: the stage was never configured for e-sign, so there is
 * nothing to seed from and the recruiter uploads the documents and writes the
 * message from empty. The result lands under `stage.adhoc_esign_documents[]`
 * rather than becoming a planned activity — `AdHocEsign` renders it.
 *
 * `stage` is the candidate's *current* stage, which is what groups the envelope.
 * An ad-hoc envelope has no stage action, so the backend stores that id on the
 * envelope itself; without it the row could not be placed and the send 422s with
 * `err_esign_stage_not_found`.
 *
 * Deliberately a sibling of `EsignSendDrawer` rather than a mode on it. They
 * differ in what they are handed and what the footer does — this one has no
 * `activityAction` at all — and the two are opened from different places by
 * different callers. Sharing the shell is worth it; sharing the props is not.
 */
export const EsignAdhocDrawer = ({
  isOpen,
  onClose,
  applicationData,
  refetchApplication,
}: EsignAdhocDrawerProps) => (
  <Drawer
    open={isOpen}
    onOpenChange={(open) => {
      if (!open) onClose();
    }}
    size="lg"
    className="!max-w-[1067px]"
    closeOnEscape
    title={
      <Flex align="center" justify="space-between" w="100%">
        <Text variant="h2">Docusign e-sign document</Text>
        <DocusignWordmarkAsset width={24} height={24} />
      </Flex>
    }
    // kula's header ships a bottom border and 16px of padding; the wizard's own
    // stepper band carries the rule directly beneath it, so both are dropped
    // rather than stacking a second divider and a second gap.
    headerClassName="!border-b-0 !px-6 !pb-0 !pt-4"
    bodyClassName="!p-0 flex min-h-0 flex-col !overflow-hidden"
  >
    {isOpen && (
      <EsignActivity
        isAdhoc
        onClose={onClose}
        // No activity — only the stage, which is what the send is grouped by.
        activityAction={{
          type: 'new',
          stage: { id: applicationData?.job_stage_id },
        }}
        jobId={applicationData?.ats_job?.id}
        applicationId={applicationData?.id}
        onSaved={refetchApplication}
      />
    )}
  </Drawer>
);
